import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import moment from "moment";
import Footer from "../components/dashboard/footer";
import Header from "../components/dashboard/header";
import Sidebar from "../components/dashboard/sidebar";
import { contentContext } from "../context/contentContext";
import SessionPopup from "../pages/SessionPopup";
import TableLoader from "../components/tableLoader";
import RevenueFilters, { RevenuePageHeader, TIMEZONE, TRANSACTION_PRESETS, money, toFilterOptions, useRevenueFilters } from "../components/revenueFilters";
import { PAYOUTS_MENU_NAME, menuIdFor } from "../utils/revenueMenu";
import "./revenue.css";

let { lambda, appname } = window.app

// /payoutsReport: `clients` is one row per client for bookings made in the range, and
// `payouts` is every Stripe payout (to a client's bank) dated in the range. Plan
// subscriptions are SpotSaver's own money and never paid out, so they are not here.

const PAGE_SIZES = [10, 25, 50];
const VIEWS = [
    { key: "clients", label: "By client" },
    { key: "history", label: "Payout history" },
];

const isZero = (v) => Math.abs(Number(v) || 0) < 0.005;

/** A money cell that fades when zero and turns red when negative. */
const Amount = ({ value, className = "" }) => {
    const n = Number(value) || 0;
    const tone = n <= -0.005 ? " revenue-negative" : isZero(n) ? " rev-zero" : "";
    return <span className={`${className}${tone}`}>{money(n)}</span>;
};

// Payout dates come from Stripe's arrival_date: a calendar day sent as midnight UTC.
// Formatting that in local time would show the day before west of UTC, so a midnight-UTC
// value is read as the date itself; anything else is a real moment in local time.
const payoutMoment = (d) => {
    const utc = moment.utc(d);
    const isCalendarDay = utc.hours() === 0 && utc.minutes() === 0 && utc.seconds() === 0 && utc.milliseconds() === 0;
    return isCalendarDay ? utc : moment(d);
};

const formatDate = (d) => (d ? payoutMoment(d).format("MMM D, YYYY") : "—");
const csvDate = (d) => (d ? payoutMoment(d).format("YYYY-MM-DD") : "");

const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const Payouts = () => {
    const navigate = useNavigate();
    const { setRoute, setActiveMenuId, userData } = useContext(contentContext);
    const [showSessionPopupup, setShowSessionPopupup] = useState(false);

    const filters = useRevenueFilters("30days");
    const { dateFrom, dateTo, client, location } = filters;
    const [view, setView] = useState("clients");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    const [report, setReport] = useState({ clients: [], payouts: [], summary: {} });
    const [filterOptions, setFilterOptions] = useState({ clients: [], locations: [] });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        if (!localStorage.getItem("token")) {
            navigate("/");
        }
        setRoute("client")
        userActivity();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const menuid = menuIdFor(userData?.permissions, PAYOUTS_MENU_NAME);
        if (menuid != null) setActiveMenuId(menuid);
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    const userActivity = () => {
        let path = window.location.pathname.split("/");
        const pageName = path[path.length - 1];
        const payload = {
            "userid": localStorage.getItem("userId"),
            "pagename": pageName,
            "pageurl": window.location.href,
            "starttime": moment(),
            "useragent": JSON.parse(localStorage.getItem("loc"))
        };
        const previousId = localStorage.getItem("previousid");
        const urlLink = lambda + '/useractivity?appname=' + appname + (previousId ? "&previousid=" + previousId : "");
        axios({ method: 'POST', url: urlLink, data: payload })
            .then(function (response) {
                if (response?.data?.statusCode === 200) {
                    localStorage.setItem("previousid", response?.data?.result)
                }
            });
    }

    // Refetch whenever the range or the client/location changes.
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        let cancelled = false;
        setLoading(true);
        setLoadError("");
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({ appname, token, fromDate: dateFrom, toDate: dateTo, timezone: TIMEZONE });
        if (client) params.set("clientid", client.value);
        if (location) params.set("storeid", location.value);
        axios({ method: 'POST', url: `${lambda}/payoutsReport?${params.toString()}` })
            .then((response) => {
                if (cancelled) return;
                const result = response?.data?.result;
                if (result === "Invalid token or Expired") {
                    setShowSessionPopupup(true);
                    return;
                }
                setReport({ clients: result?.clients || [], payouts: result?.payouts || [], summary: result?.summary || {} });
                setFilterOptions(toFilterOptions(result?.options));
            })
            .catch(() => { if (!cancelled) setLoadError("Failed to load the payouts report. Please try again."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [dateFrom, dateTo, client, location]);

    const summary = report.summary || {};
    // Failures and reversals are rare; a column of $0.00 would just be noise.
    const hasFailed = (summary.payoutFailed || 0) > 0;
    const hasReversed = (summary.reversed || 0) > 0;

    const rows = view === "clients" ? report.clients : report.payouts;
    useEffect(() => { setPage(0); }, [view, rows, pageSize]);

    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const pageRows = rows.slice(page * pageSize, page * pageSize + pageSize);
    const firstRow = rows.length === 0 ? 0 : page * pageSize + 1;
    const lastRow = Math.min(rows.length, (page + 1) * pageSize);

    const historyTotal = useMemo(() => report.payouts.reduce((acc, p) => ({
        amount: acc.amount + (p.status === "paid" ? Number(p.amount) || 0 : 0),
        bookings: acc.bookings + (Number(p.bookings) || 0),
    }), { amount: 0, bookings: 0 }), [report.payouts]);

    const kpis = [
        { label: "Client earnings", value: summary.clientShare, note: "Earned from bookings in this period", icon: "person", tone: "green" },
        { label: "Paid to bank", value: summary.paidToBank, note: "Landed in clients' bank accounts", icon: "account_balance", tone: "green", valueTone: "positive" },
        { label: "In Stripe balance", value: summary.inStripeBalance, note: "Transferred, waiting for Stripe's payout", icon: "hourglass_top", tone: "lilac" },
        { label: "Awaiting transfer", value: summary.awaitingTransfer, note: "Held by SpotSaver — client not payout-ready", icon: "schedule", tone: "amber" },
        ...(hasFailed ? [{ label: "Payout failed", value: summary.payoutFailed, note: "Stripe could not pay the client's bank", icon: "error", tone: "amber", valueTone: "negative" }] : []),
    ];

    const handleExport = () => {
        const scope = [client?.label, location?.label].filter(Boolean).join("-").replace(/[^a-z0-9-]+/gi, "_");
        const suffix = `${dateFrom}-to-${dateTo}${scope ? `-${scope}` : ""}`;
        if (view === "clients") {
            const header = ["Client", "Email", "Bookings", "Gross bookings", "Client earnings", "Paid to bank", "In Stripe balance",
                "Awaiting transfer", ...(hasFailed ? ["Payout failed"] : []), ...(hasReversed ? ["Reversed"] : []),
                "SpotSaver commission", "Last payout"];
            const body = report.clients.map((r) => [r.name, r.email, r.bookings, r.grossBookings, r.clientShare, r.paidToBank,
                r.inStripeBalance, r.awaitingTransfer, ...(hasFailed ? [r.payoutFailed] : []), ...(hasReversed ? [r.reversed] : []),
                r.commission, csvDate(r.lastPayoutDate)]);
            downloadCsv(`spotsaver-payouts-by-client-${suffix}.csv`, [header, ...body]);
        } else {
            const header = ["Date", "Client", "Payout ID", "Bookings", "Amount", "Status", "Failure reason"];
            const body = report.payouts.map((p) => [csvDate(p.date), p.clientName, p.payoutId,
                p.bookings, p.amount, p.status === "failed" ? "Failed" : "Paid", p.failureReason]);
            downloadCsv(`spotsaver-payout-history-${suffix}.csv`, [header, ...body]);
        }
    };

    const clientCols = 9 + (hasFailed ? 1 : 0) + (hasReversed ? 1 : 0);

    const renderClientCells = (r) => (
        <>
            <td className="rev-num">{r.bookings}</td>
            <td className="rev-num"><Amount value={r.grossBookings} /></td>
            <td className="rev-num rev-strong rev-group-start"><Amount value={r.clientShare} /></td>
            <td className="rev-num"><Amount value={r.paidToBank} /></td>
            <td className="rev-num"><Amount value={r.inStripeBalance} /></td>
            <td className="rev-num"><Amount value={r.awaitingTransfer} /></td>
            {hasFailed && <td className="rev-num"><Amount value={r.payoutFailed} className={r.payoutFailed > 0 ? "revenue-negative" : ""} /></td>}
            {hasReversed && <td className="rev-num"><Amount value={r.reversed} /></td>}
            <td className="rev-num rev-brand rev-group-start"><Amount value={r.commission} /></td>
        </>
    );

    const emptyRow = (cols, message) => (
        <tr>
            <td colSpan={cols} className="rev-empty">
                <span className="material-symbols-outlined" aria-hidden="true">payments</span>
                {message}
            </td>
        </tr>
    );

    return (
        <>
            {showSessionPopupup && <SessionPopup />}
            <div id="layout-wrapper">
                <Header breadcrumb={["Finance", "Payouts"]} />
                <Sidebar />

                <div className="main-content revenue-scope revenue-page">
                    <div className="page-content">
                        <div className="container-fluid">

                            <RevenuePageHeader
                                title="Payouts"
                                subtitle="What clients earned from bookings, and whether it has reached their bank."
                                onReset={filters.reset}
                                onExport={handleExport}
                                exportDisabled={loading || rows.length === 0}
                            />

                            <RevenueFilters
                                filters={filters}
                                clientOptions={filterOptions.clients}
                                locationOptions={filterOptions.locations}
                                presets={TRANSACTION_PRESETS}
                            />

                            {loadError && <div className="alert alert-danger">{loadError}</div>}

                            <div className={`rev-kpis${loading ? " is-loading" : ""}`}>
                                {kpis.map((k) => (
                                    <div className="rev-card rev-kpi" key={k.label}>
                                        <div className="rev-kpi-head">
                                            <p className="rev-kpi-label">{k.label}</p>
                                            <span className={`rev-kpi-icon rev-kpi-icon--${k.tone}`} aria-hidden="true">
                                                <span className="material-symbols-outlined">{k.icon}</span>
                                            </span>
                                        </div>
                                        <p className={`rev-kpi-value${k.valueTone ? ` rev-kpi-value--${k.valueTone}` : ""}`}>{money(k.value)}</p>
                                        <p className="rev-kpi-note">{k.note}</p>
                                    </div>
                                ))}
                            </div>

                            <section className="rev-card rev-breakdown">
                                <div className="rev-breakdown-head">
                                    <div>
                                        <h2 className="rev-breakdown-title">{view === "clients" ? "Client payouts" : "Payout history"}</h2>
                                        <p className="rev-breakdown-subtitle">
                                            {view === "clients"
                                                ? "Bookings made in this period, by the client they belong to."
                                                : "Stripe payouts to clients' banks made in this period."}
                                        </p>
                                    </div>
                                    <div className="rev-breakdown-actions">
                                        <div className="revenue-presets revenue-presets--small" role="group" aria-label="View">
                                            {VIEWS.map((v) => (
                                                <button
                                                    key={v.key}
                                                    type="button"
                                                    className={`revenue-preset${view === v.key ? " active" : ""}`}
                                                    aria-pressed={view === v.key}
                                                    onClick={() => setView(v.key)}
                                                >
                                                    {v.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="rev-table-wrap">
                                    {view === "clients" ? (
                                        <table className="rev-table">
                                            <thead>
                                                <tr className="rev-table-groups">
                                                    <th rowSpan={2} className="rev-table-date">Client</th>
                                                    <th colSpan={2}>Bookings</th>
                                                    <th colSpan={4 + (hasFailed ? 1 : 0) + (hasReversed ? 1 : 0)} className="rev-group-start">Client share</th>
                                                    <th rowSpan={2} className="rev-num rev-strong rev-group-start">SpotSaver<br />commission</th>
                                                    <th rowSpan={2} className="rev-num">Last payout</th>
                                                </tr>
                                                <tr>
                                                    <th className="rev-num">Count</th>
                                                    <th className="rev-num">Gross</th>
                                                    <th className="rev-num rev-strong rev-group-start">Earned</th>
                                                    <th className="rev-num">Paid to bank</th>
                                                    <th className="rev-num">In Stripe</th>
                                                    <th className="rev-num">Awaiting</th>
                                                    {hasFailed && <th className="rev-num">Failed</th>}
                                                    {hasReversed && <th className="rev-num">Reversed</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan={clientCols}><TableLoader /></td></tr>
                                                ) : pageRows.length === 0 ? (
                                                    emptyRow(clientCols, "No bookings for the selected filters")
                                                ) : pageRows.map((r) => (
                                                    <tr key={r.clientid || "none"}>
                                                        <td className="rev-table-date">
                                                            <div className="rev-client-name">{r.name}</div>
                                                            {r.email && <div className="rev-client-email">{r.email}</div>}
                                                        </td>
                                                        {renderClientCells(r)}
                                                        <td className="rev-num">{formatDate(r.lastPayoutDate)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            {!loading && report.clients.length > 0 && (
                                                <tfoot>
                                                    <tr>
                                                        <td className="rev-table-date">Total</td>
                                                        {renderClientCells(summary)}
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    ) : (
                                        <table className="rev-table">
                                            <thead>
                                                <tr>
                                                    <th className="rev-table-date">Date</th>
                                                    <th>Client</th>
                                                    <th>Payout ID</th>
                                                    <th className="rev-num">Bookings</th>
                                                    <th className="rev-num rev-strong">Amount</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan={6}><TableLoader /></td></tr>
                                                ) : pageRows.length === 0 ? (
                                                    emptyRow(6, "No payouts to clients' banks in this period")
                                                ) : pageRows.map((p) => (
                                                    <tr key={p.payoutId}>
                                                        <td className="rev-table-date">{formatDate(p.date)}</td>
                                                        <td>{p.clientName}</td>
                                                        <td className="rev-mono">{p.payoutId}</td>
                                                        <td className="rev-num">{p.bookings}</td>
                                                        <td className="rev-num rev-strong"><Amount value={p.amount} /></td>
                                                        <td>
                                                            <span
                                                                className={`rev-status rev-status--${p.status}`}
                                                                title={p.status === "failed" && p.failureReason ? p.failureReason : undefined}
                                                            >
                                                                {p.status === "failed" ? "Failed" : "Paid"}
                                                            </span>
                                                            {p.status === "failed" && p.failureReason && (
                                                                <div className="rev-client-email">{p.failureReason}</div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            {!loading && report.payouts.length > 0 && (
                                                <tfoot>
                                                    <tr>
                                                        <td className="rev-table-date">Total paid</td>
                                                        <td />
                                                        <td />
                                                        <td className="rev-num">{historyTotal.bookings}</td>
                                                        <td className="rev-num rev-strong"><Amount value={historyTotal.amount} /></td>
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    )}
                                </div>

                                {view === "history" && (
                                    <p className="rev-footnote">
                                        Amounts are the client share of the bookings each payout carried. Stripe's own payout
                                        total can differ if it also swept other balance on the client's account.
                                    </p>
                                )}

                                <div className="rev-pagination">
                                    <label>
                                        Rows per page
                                        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </label>
                                    <span>{firstRow}–{lastRow} of {rows.length}</span>
                                    <div className="rev-pagination-nav">
                                        <button type="button" aria-label="Previous page" disabled={page === 0} onClick={() => setPage(page - 1)}>
                                            <span className="material-symbols-outlined">chevron_left</span>
                                        </button>
                                        <button type="button" aria-label="Next page" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>
                                            <span className="material-symbols-outlined">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>

                    <Footer />
                </div>
            </div>
        </>
    );
};

export default Payouts;
